"use client";

import React from "react";
import { Check, X, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDealHooks } from "@/features/deals/hooks/use-deals-hooks/entity-hooks";
import type { Deal } from "@/lib/types/database/deals";
import { formatCurrency as formatCurrencyShared } from "@west-tennessee-consulting/ui";

/**
 * Inline-editable deal cost. When a cost is on file it renders the formatted
 * currency with a hover pencil; when it's missing the "Not set" placeholder is
 * paired with an "Add cost" button that flips a numeric input open in place — so
 * the user can fill the missing data without leaving this tab. Persists via
 * useDealHooks().useUpdate() (which serializes the BigFloat column). Only `cost`
 * is editable — `revenue` and `margin`/`margin_percentage` (derived) are left as-is.
 */
function EditableDealCost({ deal }: { deal: Deal }) {
  const { useUpdate } = useDealHooks();
  // Aliased to `isSaving`: a MUTATION's pending flag (the user pressed Save), not
  // a background query. TYPING_FOCUS_STABILITY forbids gating a text field on the
  // latter; the generic name could not distinguish them.
  const { mutateAsync, isPending: isSaving } = useUpdate();

  const formatCurrency = React.useCallback(
    (value: number) => formatCurrencyShared(value, { currency: deal.currency ?? "USD" }),
    [deal.currency]
  );

  // Local mirror so the saved value shows immediately even without a refetch.
  const [value, setValue] = React.useState<number | null>(deal.cost ?? null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<string>(
    deal.cost != null ? String(deal.cost) : ""
  );

  // Re-seed when the underlying deal changes (e.g. navigating between deals).
  React.useEffect(() => {
    setValue(deal.cost ?? null);
    if (!isEditing) setDraft(deal.cost != null ? String(deal.cost) : "");
  }, [deal.id, deal.cost]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => {
    setDraft(value != null ? String(value) : "");
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(value != null ? String(value) : "");
    setIsEditing(false);
  };

  const save = async () => {
    const trimmed = draft.trim();

    // Empty input is treated as "no change" (the deal's cost column is typed
    // `number`, not `number | null`, so we don't clear it back to empty here —
    // leaving the field blank simply cancels the edit).
    if (trimmed === "") {
      setIsEditing(false);
      return;
    }

    const nextCost = Number(trimmed);
    if (Number.isNaN(nextCost) || nextCost < 0) {
      toast.error("Enter a valid cost amount");
      return;
    }

    if (nextCost === value) {
      setIsEditing(false);
      return;
    }

    try {
      await mutateAsync({ id: deal.id, cost: nextCost });
      setValue(nextCost);
      setIsEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update cost");
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          step="0.01"
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="0.00"
          autoFocus
          disabled={isSaving}
          className="h-9 w-32 text-base md:text-lg"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            } else if (e.key === "Enter") {
              e.preventDefault();
              void save();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          onClick={() => void save()}
          disabled={isSaving}
        >
          <Check className="h-4 w-4" />
          <span className="sr-only">Save</span>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={cancel}
          disabled={isSaving}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Cancel</span>
        </Button>
      </div>
    );
  }

  if (value != null) {
    return (
      <div className="group/cost flex items-center justify-end gap-1 md:justify-start">
        <p className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
          {formatCurrency(value)}
        </p>
        <button
          type="button"
          onClick={startEdit}
          className="opacity-0 group-hover/cost:opacity-100 focus:opacity-100 transition-opacity text-gray-400 hover:text-gray-600"
          aria-label="Edit cost"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={startEdit}
      className="h-8 px-2 text-gray-600 dark:text-gray-400"
    >
      <Plus className="h-4 w-4 mr-1" />
      Add cost
    </Button>
  );
}

export function DealProjectHealthTab({ item }: { item: Deal }) {
  const getStatusBadgeColor = (status?: string) => {
    switch (status) {
      case "red":
        return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
      case "yellow":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
      case "green":
        return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case "red":
        return "Red - At Risk";
      case "yellow":
        return "Yellow - Warning";
      case "green":
        return "Green - Healthy";
      default:
        return "Not Set";
    }
  };

  const healthDimensions = [
    {
      label: "Schedule Health",
      description: "Project timeline adherence",
      value: item.schedule_status,
    },
    {
      label: "Budget/Margin Health",
      description: "Financial performance",
      value: item.budget_status,
    },
    {
      label: "Scope Control Health",
      description: "Scope creep management",
      value: item.scope_status,
    },
    {
      label: "Client Alignment Health",
      description: "Client satisfaction",
      value: item.alignment_status,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-3 md:p-4 bg-blue-50 dark:bg-blue-950/40">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Financial Summary</h3>
        <div className="flex flex-col gap-2 md:grid md:grid-cols-3 md:gap-4">
          <div className="flex items-baseline justify-between gap-2 md:block">
            <label className="text-xs text-gray-600 dark:text-gray-400 shrink-0">Revenue</label>
            <p className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 text-right md:text-left">
              {formatCurrencyShared(item.revenue, { currency: item.currency ?? "USD" })}
            </p>
          </div>
          <div className="flex items-baseline justify-between gap-2 md:block">
            <label className="text-xs text-gray-600 dark:text-gray-400 shrink-0">Cost</label>
            <div className="md:mt-0.5">
              <EditableDealCost deal={item} />
            </div>
          </div>
          <div className="flex items-baseline justify-between gap-2 md:block">
            <label className="text-xs text-gray-600 dark:text-gray-400 shrink-0">Margin</label>
            <p className={`text-base md:text-lg font-semibold text-right md:text-left ${
              item.margin_percentage
                ? item.margin_percentage >= 30
                  ? "text-green-600 dark:text-green-400"
                  : item.margin_percentage >= 15
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-red-600 dark:text-red-400"
                : "text-gray-600 dark:text-gray-400"
            }`}>
              {item.margin_percentage ? `${item.margin_percentage}%` : "N/A"}
            </p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Project Health Dimensions</h3>
        <div className="space-y-2">
          {healthDimensions.map((dimension) => (
            <div key={dimension.label} className="border rounded-lg p-3 md:p-4">
              <div className="flex items-start justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">{dimension.label}</label>
                  <p className="text-xs text-gray-500 mt-1">{dimension.description}</p>
                </div>
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 md:px-3 text-xs md:text-sm font-medium ${getStatusBadgeColor(dimension.value ?? undefined)}`}>
                  {getStatusLabel(dimension.value ?? undefined)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-sm text-gray-500 mt-6">
        Project health status helps track the overall health of the deal across different dimensions. Use the "Manage Health Status" action to update these values.
      </p>
    </div>
  );
}
